/**
 * @file OrderNextStepCard.tsx
 * @description Блок «Что дальше» справа от пути заказа
 * (phase19.24-order-card-chain-design.md §5, §13).
 *
 * Когда ход менеджера, главная кнопка живёт в полосе «Сейчас» и в шапке —
 * здесь остаются напоминания (срок стадии, просрочка) и второстепенные
 * действия: написать клиенту, открыть карточку клиента, запросить отзыв. Так
 * соблюдается правило DS §5.3 «одна главная кнопка»: второй залитой кнопки на
 * экране нет.
 *
 * Чужой ход бывает двух видов, и путать их нельзя (замечание 2026-09-21):
 * без кнопки — «От вас сейчас ничего не требуется»; с кнопкой (клиент придёт
 * за самовывозом, деньги принимает менеджер) — «Ждём клиента. Когда придёт —
 * «Отметить оплату»». Формулировку даёт реестр владельцев, а не `if` здесь.
 *
 * «Написать клиенту» (SC64) открывает окно «Сообщения» на диалоге этого
 * заказа (найти или создать), не уводя со страницы; без права переписки
 * кнопки нет.
 */

import { AlarmClock, CalendarClock, MessageSquare, Send, UserRound } from "lucide-react";
import { Link, useParams } from "react-router";

import type { OrderActionKey } from "../../lib/order-actions";
import type { OrderRouteStageKind } from "../../lib/order-chain";
import { ownerMeta } from "../../lib/order-chain-owner";
import type { OrderNowModel, ResolvedPrimaryAction } from "../../lib/order-now";
import { useChatWindow } from "../chat/chat-window-context";
import { Button } from "../ui/button";
import { EditorialCard } from "../ui/EditorialCard";
import { SectionLabel } from "../ui/SectionLabel";
import { formatDocDateTime } from "../wms/wms-document-meta";

interface Props {
  now: OrderNowModel | null;
  /** Главная кнопка стадии — нужна только для текста «когда очередь вернётся». */
  primary: ResolvedPrimaryAction | null;
  /** Ссылки второстепенных действий; без слага их не строим. */
  slug: string | undefined;
  customerId: string;
  canManage: boolean;
  onAction: (action: OrderActionKey) => void;
}

/** Стадии, где отзыв уже имеет смысл просить: заказ у клиента на руках. */
const REVIEW_STAGES: readonly OrderRouteStageKind[] = ["DELIVERED", "COMPLETED"];

/** Две строки блока: что происходит и что делать (или почему делать нечего). */
function nextStepText(
  now: OrderNowModel,
  primary: ResolvedPrimaryAction | null,
): { first: string; second: string } {
  if (now.isManagerTurn) {
    return {
      first: now.text.first,
      second: now.text.second ?? "Действие выполняется здесь же",
    };
  }
  const meta = ownerMeta(now.owner);
  if (primary) {
    return {
      first: meta.waiting,
      second: `${meta.waitingAction} — «${primary.label}»`,
    };
  }
  return {
    first: "От вас сейчас ничего не требуется",
    second: now.nextStepHint ?? now.text.second ?? "Заказ ведёт другой участник",
  };
}

export function OrderNextStepCard({
  now,
  primary,
  slug,
  customerId,
  canManage,
  onAction,
}: Props) {
  const chat = useChatWindow();
  // Карточка живёт на странице заказа `orders/:id` — заказ берём из адреса.
  const { id: orderId } = useParams<{ id: string }>();
  const deadline = now?.stage.deadlineAt ?? null;
  const text = now ? nextStepText(now, primary) : null;
  // Отзыв просят с доставленного заказа; на главной кнопке он уже может быть.
  const showReview =
    canManage &&
    !!now &&
    REVIEW_STAGES.includes(now.stage.kind) &&
    primary?.action !== "requestReview";

  return (
    <EditorialCard className="lg:sticky lg:top-4">
      <EditorialCard.Body className="flex flex-col gap-3">
        <SectionLabel as="h2">Что дальше</SectionLabel>

        {text ? (
          <>
            <p className="text-base font-medium">{text.first}</p>
            <p className="text-sm text-on-surface-variant">{text.second}</p>
          </>
        ) : (
          <p className="text-sm text-on-surface-variant">
            Путь заказа не загрузился — действия доступны в меню «⋯» шапки.
          </p>
        )}

        {now?.overdueLabel ? (
          <p className="flex items-center gap-2 text-sm text-destructive">
            <AlarmClock className="size-4 shrink-0" aria-hidden />
            {now.overdueLabel}
          </p>
        ) : null}

        {deadline ? (
          <p className="flex items-center gap-2 text-sm text-warning">
            <CalendarClock className="size-4 shrink-0" aria-hidden />
            Срок стадии: {formatDocDateTime(deadline)}
          </p>
        ) : null}

        <div className="flex flex-col gap-2 pt-1">
          {showReview ? (
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => onAction("requestReview")}
            >
              <Send className="size-4" aria-hidden />
              Запросить отзыв
            </Button>
          ) : null}
          {slug ? (
            <>
              {chat.enabled ? (
                <Button
                  variant="outline"
                  className="justify-start"
                  onClick={() => chat.openChat({ customerId, orderId })}
                >
                  <MessageSquare className="size-4" aria-hidden />
                  Написать клиенту
                </Button>
              ) : null}
              <Button variant="ghost" className="justify-start" asChild>
                <Link to={`/shop/${slug}/customers/${customerId}`}>
                  <UserRound className="size-4" aria-hidden />
                  Открыть клиента
                </Link>
              </Button>
            </>
          ) : null}
        </div>
      </EditorialCard.Body>
    </EditorialCard>
  );
}
